import { CountryCode, CountryCodeSchema } from "@/data/country-data"
import { SHIPPING_COUNTRY_CODES } from "@/data/shipping-country-code"
import { apacLocales, euLocales, jpLocales, naLocales } from "@/i18n.config"
import centraPath from "@/lib/centra-path/centra-path-init"
import { NextRequest } from "next/server"

const EU_COUNTRIES_WITHOUT_AVAILABLE_SHIPPING_COUNTRIES: CountryCode[] = [
    "AT",
    "BE",
    "BG",
    "CY",
    "DE",
    "EE",
    "FI",
    "FR",
    "GR",
    "HR",
    "HU",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "PT",
    "RO",
    "SK",
    "SI",
    "ES"
]

export const extractUserCountryFromCentraLocaleOrUserCountry = (
    request: NextRequest,
    userCountry: string
) => {
    const centraLocale =
        centraPath.extractCentraLocaleFromPath(request.nextUrl.pathname) || "de"
    if (centraLocale.includes("-")) {
        const parts = centraLocale.split("-")
        return parts[1].toUpperCase()
    }

    /**
     * If the locale is "fr", we should always set the country to "fr" regardless of the user's country.
     * If the locale is "en", we should set the country to the user's country if it's not already set.
     * If the locale is "de", we should set the country to the user's country if it's not already set.
     */
    if (centraLocale === "fr") {
        return SHIPPING_COUNTRY_CODES.France.toLocaleUpperCase()
    }
    if (centraLocale === "de" || centraLocale === "en") {
        const normalizedUserCountry = userCountry.toUpperCase()

        return EU_COUNTRIES_WITHOUT_AVAILABLE_SHIPPING_COUNTRIES.includes(
            normalizedUserCountry as CountryCode
        )
            ? normalizedUserCountry
            : SHIPPING_COUNTRY_CODES.Germany.toLocaleUpperCase()
    }
    return SHIPPING_COUNTRY_CODES.Germany.toLocaleUpperCase()
}

export const isValidCountryCode = (code: string): code is CountryCode => {
    const validCodes: CountryCode[] = CountryCodeSchema.options
    return validCodes.includes(code as CountryCode)
}

const allLocales = [...euLocales, ...apacLocales, ...naLocales, ...jpLocales]

/**
 * Used in the checkout to get the centra locale from the country code
 */
export function getCentraLocaleFromCountryCode(countryCode: string): string {
    const lowerCode = countryCode.toLowerCase()

    const found = allLocales.find(locale =>
        locale.toLowerCase().endsWith(`-${lowerCode}`)
    )
    if (found) return `/${found}`

    if (lowerCode === "fr") return "/fr"
    if (lowerCode === "de") return "/de"
    if (lowerCode === "en") return "/en"

    return "/en"
}
